// ==============================================
// Profile Dropdown Component
// ==============================================

import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../providers/AuthProvider';
import { useLogout } from '../../hooks/useLogout';
import clsx from 'clsx';

export function ProfileDropdown() {
  const { user, isAuthenticated } = useAuth();
  const logout = useLogout();

  if (!isAuthenticated || !user) {
    return null;
  }

  const isAdmin = user.role === 'admin' || user.role === 'super_admin';

  const menuItems = [
    { label: 'Profile', href: '/dashboard/profile', icon: UserCircleIcon },
    { label: 'Billing & Plan', href: '/dashboard/billing', icon: CreditCardIcon },
    { label: 'Settings', href: '/dashboard/settings', icon: Cog6ToothIcon },
    ...(isAdmin ? [{ label: 'Admin Panel', href: '/admin', icon: ShieldCheckIcon }] : []),
  ];

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors">
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={user.name || 'Profile'}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-semibold">
            {(user.name || user.email || '?')[0].toUpperCase()}
          </div>
        )}
        <span className="hidden sm:inline-block max-w-[120px] truncate">
          {user.name || user.email}
        </span>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] origin-top-right rounded-xl bg-slate-800 border border-slate-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="px-4 py-3 border-b border-slate-700">
            <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
            <p className="text-xs text-slate-400 truncate">{user.email}</p>
          </div>

          <div className="py-1">
            {menuItems.map((item) => (
              <Menu.Item key={item.href}>
                {({ active }) => (
                  <Link
                    to={item.href}
                    className={clsx(
                      'flex items-center gap-3 px-4 py-2 text-sm',
                      active ? 'bg-slate-700 text-white' : 'text-slate-300'
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                )}
              </Menu.Item>
            ))}
          </div>

          <div className="py-1 border-t border-slate-700">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => void logout()}
                  className={clsx(
                    'flex w-full items-center gap-3 px-4 py-2 text-sm',
                    active ? 'bg-slate-700 text-white' : 'text-slate-300'
                  )}
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                  Sign out
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default ProfileDropdown;
